import { Link, useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  admitCustodianPackage,
  getCustodianAdmissionStatus,
  getCustodianPackage,
  submitCustodianAuthorityDecision,
} from '../../api/custodianApi';
import { CustodianConfirmModal } from '../../components/custodian/CustodianConfirmModal';
import { CustodianSourceEvidencePanel } from '../../components/custodian/CustodianSourceEvidencePanel';
import { EvidenceChecklistCard } from '../../components/custodian/EvidenceChecklistCard';
import { Button } from '../../components/ui/Button';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { LifecycleInfoPopover } from '../../components/ui/LifecycleInfoPopover';
import { NoticeBanner } from '../../components/ui/NoticeBanner';
import { StatusPill } from '../../components/ui/StatusPill';
import { isUuid } from '../../utils/format';
import { rememberAdmittedPackageId } from './custodianAdmittedSession';
import {
  UNAVAILABLE,
  canAdmitPackage,
  canRecordAuthorityDecision,
  custodianWorkflowSteps,
  displayValue,
  custodianEvidenceChecklistItems,
  evidenceBundleLabel,
  evidenceCreationStatus,
  evidenceReadinessLabel,
  isPackageAdmitted,
  packageAdmission,
  packageAuthority,
  packageCustody,
} from './custodianReviewLabels';

const evidenceChecklist = [
  'Preserved original hash',
  'Signature/tamper report',
  'Provider manifest',
  'Admission receipt',
  'Counterparty context',
];

const actionLinks = [
  { label: 'Issue certificate of custody' },
  { label: 'Open copy request', to: '/custodian/release-copy' },
];

const tabs = [
  { id: 'overview', label: 'Overview' },
  { id: 'evidence', label: 'Evidence' },
  { id: 'timeline', label: 'Custody timeline' },
  { id: 'certificates', label: 'Certificates' },
  { id: 'related', label: 'Related actions' },
  { id: 'audit', label: 'Audit' },
];

/** @typedef {{ label: string, date: string, dot: 'green' | 'gold' }} TimelineStep */

/** Mock detail keyed by asset id — wireframe A-18802 + register peers */
const DETAIL_BY_ASSET = {
  'A-18802': {
    borrower: 'Apex HVAC',
    originator: 'Originator',
    facility: 'UMB Line A',
    custodyState: 'Authoritative active',
    badgeVariant: 'active',
    legalType: 'Electronic chattel paper',
    authorityDecision: 'Approved',
    evidenceState: 'Complete and sealed',
    holderSummary: 'Custodian',
    nextAllowed: 'Pledge, transfer, copy, retire',
    receiptRef: 'RC-22190',
    bundleRef: 'EV-8821',
    /** @type {TimelineStep[]} */
    timeline: [
      { label: 'Submitted', date: '03/01', dot: 'green' },
      { label: 'Reviewed', date: '03/02', dot: 'green' },
      { label: 'Admitted', date: '03/02', dot: 'green' },
      { label: 'Pledged', date: '03/03', dot: 'gold' },
      { label: 'Released', date: '03/04', dot: 'gold' },
      { label: 'Active', date: '03/04', dot: 'green' },
    ],
  },
  'A-18788': {
    borrower: 'Pioneer Equip.',
    originator: 'Originator',
    facility: 'UMB Line A',
    custodyState: 'Pledged',
    badgeVariant: 'pledged',
    legalType: 'Electronic chattel paper',
    authorityDecision: 'Approved',
    evidenceState: 'Complete and sealed',
    holderSummary: 'Custodian',
    nextAllowed: 'Release, transfer, copy',
    receiptRef: 'PL-11807',
    bundleRef: 'EV-8799',
    timeline: [
      { label: 'Submitted', date: '02/20', dot: 'green' },
      { label: 'Admitted', date: '02/21', dot: 'green' },
      { label: 'Pledged', date: '02/22', dot: 'gold' },
    ],
  },
  'A-18751': {
    borrower: 'Summit Foods',
    originator: 'Originator',
    facility: 'SPV 2026-1',
    custodyState: 'Transferred',
    badgeVariant: 'transferred',
    legalType: 'Electronic chattel paper',
    authorityDecision: 'Approved',
    evidenceState: 'Archived to trustee',
    holderSummary: 'Owner trustee',
    nextAllowed: 'Monitor · servicing visibility',
    receiptRef: 'TR-22881',
    bundleRef: 'EV-7701',
    timeline: [
      { label: 'Submitted', date: '01/10', dot: 'green' },
      { label: 'Admitted', date: '01/11', dot: 'green' },
      { label: 'Transferred', date: '01/15', dot: 'green' },
    ],
  },
  'A-18721': {
    borrower: 'Nova Med',
    originator: 'Originator',
    facility: '—',
    custodyState: 'Repository only',
    badgeVariant: 'repo',
    legalType: 'E-contract',
    authorityDecision: 'Repository classification',
    evidenceState: 'Sealed (non-authoritative)',
    holderSummary: 'Custodian — repository view',
    nextAllowed: 'Upgrade path / admit review',
    receiptRef: '—',
    bundleRef: 'EV-6612',
    timeline: [
      { label: 'Submitted', date: '02/01', dot: 'green' },
      { label: 'Classified', date: '02/02', dot: 'gold' },
    ],
  },
  'A-18697': {
    borrower: 'Credo Retail',
    originator: 'Originator',
    facility: 'UMB Line A',
    custodyState: 'Release pending',
    badgeVariant: 'pending',
    legalType: 'Electronic chattel paper',
    authorityDecision: 'Approved',
    evidenceState: 'Complete — payoff pending',
    holderSummary: 'Custodian',
    nextAllowed: 'Complete release, issue receipt',
    receiptRef: 'RL-9901',
    bundleRef: 'EV-8804',
    timeline: [
      { label: 'Submitted', date: '03/05', dot: 'green' },
      { label: 'Admitted', date: '03/06', dot: 'green' },
      { label: 'Release requested', date: '03/07', dot: 'gold' },
    ],
  },
};

function defaultDetail(assetId) {
  return {
    borrower: 'Unknown borrower',
    originator: '—',
    facility: '—',
    custodyState: 'Under review',
    badgeVariant: 'pending',
    legalType: '—',
    authorityDecision: 'Pending',
    evidenceState: 'In progress',
    holderSummary: 'Custodian',
    nextAllowed: 'Awaiting packaging',
    receiptRef: '—',
    bundleRef: '—',
    timeline: [
      { label: 'Submitted', date: '—', dot: 'gold' },
      { label: 'Custody review', date: '—', dot: 'gold' },
    ],
  };
}

function badgeClass(variant) {
  switch (variant) {
    case 'active':
      return 'bg-evault-vault-active/15 text-evault-vault-active ring-1 ring-evault-vault-active/35';
    case 'pledged':
      return 'bg-evault-gold/15 text-evault-gold ring-1 ring-evault-gold/40';
    case 'transferred':
      return 'bg-evault-teal-deep/10 text-evault-teal-deep ring-1 ring-evault-teal-deep/25';
    case 'repo':
      return 'bg-umb-neutral-mid/15 text-umb-neutral-dark/60 ring-1 ring-evault-border';
    case 'rejected':
      return 'bg-red-500/10 text-red-600 ring-1 ring-red-500/25';
    case 'pending':
    default:
      return 'bg-evault-navy/10 text-evault-navy ring-1 ring-evault-navy/25';
  }
}

function formatShortDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { month: '2-digit', day: '2-digit' });
}

function formatTimestamp(value) {
  if (!value) return UNAVAILABLE;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return UNAVAILABLE;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function liveBadgeVariant(pkg, admissionStatus) {
  const authority = packageAuthority(pkg);
  if (isPackageAdmitted(pkg, admissionStatus)) {
    return 'active';
  }
  if (authority === 'REJECTED') return 'rejected';
  return 'pending';
}

function liveNextAllowed(pkg) {
  if (canAdmitPackage(pkg)) return 'Admit to Authoritative Vault';
  if (canRecordAuthorityDecision(pkg)) return 'Record Custodian authority decision';
  const authority = packageAuthority(pkg);
  if (authority === 'REJECTED') return 'Admission is not available';
  if (pkg?.admission_state === 'AUTHORITATIVE') return 'Admitted';
  return UNAVAILABLE;
}

function liveTimeline(pkg) {
  /** @type {TimelineStep[]} */
  const steps = [
    { label: 'Submitted', date: formatShortDate(pkg?.created_at), dot: 'green' },
  ];
  if (pkg?.routed_at) {
    steps.push({
      label: 'Routed',
      date: formatShortDate(pkg.routed_at),
      dot: 'green',
    });
  }
  const authority = packageAuthority(pkg);
  if (authority === 'PENDING') {
    steps.push({ label: 'Authority', date: 'Pending', dot: 'gold' });
  } else if (authority === 'APPROVED' || authority === 'REJECTED') {
    steps.push({
      label: authority === 'APPROVED' ? 'Approved' : 'Rejected',
      date: formatShortDate(pkg?.updated_at),
      dot: authority === 'REJECTED' ? 'gold' : 'green',
    });
  }
  if (pkg?.admission_state === 'AUTHORITATIVE') {
    steps.push({
      label: 'Admitted',
      date: formatShortDate(pkg?.updated_at),
      dot: 'green',
    });
  } else if (authority === 'APPROVED') {
    steps.push({ label: 'Admission', date: 'Pending', dot: 'gold' });
  }
  return steps;
}

function viewModelFromPackage(pkg, admissionStatus) {
  const authority = packageAuthority(pkg);
  const custody = packageCustody(pkg);
  return {
    borrower: displayValue(pkg?.borrower),
    originator: displayValue(pkg?.originator),
    facility: displayValue(pkg?.program),
    custodyState: displayValue(custody),
    badgeVariant: liveBadgeVariant(pkg, admissionStatus),
    legalType: UNAVAILABLE,
    authorityDecision: displayValue(authority),
    evidenceState: evidenceReadinessLabel(pkg),
    holderSummary: displayValue(pkg?.current_holder),
    nextAllowed: liveNextAllowed(pkg),
    receiptRef: UNAVAILABLE,
    bundleRef: evidenceBundleLabel(pkg),
    timeline: liveTimeline(pkg),
  };
}

function TimelineDot({ tone }) {
  if (tone === 'gold') {
    return (
      <span
        className="h-2 w-2 shrink-0 rounded-full border border-[#FFE8A3] bg-[#987200]"
        aria-hidden
      />
    );
  }
  return (
    <span className="h-2 w-2 shrink-0 rounded-full bg-eorig-navy-600" aria-hidden />
  );
}

function HorizontalCustodyTimeline({ steps }) {
  return (
    <ol
      className="flex w-full min-w-0 items-center overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      aria-label="Custody timeline"
    >
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        return (
          <li
            key={`${step.label}-${step.date}-${index}`}
            className={`flex items-center gap-4 ${isLast ? 'shrink-0' : 'flex-1 pr-3'}`}
          >
            <div className="flex shrink-0 items-center gap-2">
              <TimelineDot tone={step.dot} />
              <p className="whitespace-nowrap text-sm font-semibold leading-[21px] text-[#1F2933]">
                {step.label}
              </p>
            </div>
            {isLast ? null : (
              <span
                className="h-[2px] min-w-[40px] flex-1"
                style={{
                  backgroundImage:
                    'repeating-linear-gradient(90deg, #D1D5DB 0, #D1D5DB 4px, transparent 4px, transparent 8px)',
                }}
                aria-hidden
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

export function AssetCertificationDetailPage() {
  const { assetId } = useParams();
  const id = assetId ? decodeURIComponent(assetId) : '—';
  const live = isUuid(id);

  const mockDetail = useMemo(() => {
    return DETAIL_BY_ASSET[id] ?? defaultDetail(id);
  }, [id]);

  const [tab, setTab] = useState('overview');
  const [pkg, setPkg] = useState(null);
  const [admissionStatus, setAdmissionStatus] = useState(null);
  const [loading, setLoading] = useState(live);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [confirm, setConfirm] = useState(null);

  const loadLive = useCallback(async () => {
    if (!live) return;
    setLoading(true);
    setError('');
    try {
      const [packageResponse, statusResponse] = await Promise.all([
        getCustodianPackage(id),
        getCustodianAdmissionStatus(id).catch(() => null),
      ]);
      setPkg(packageResponse);
      setAdmissionStatus(statusResponse);
      if (isPackageAdmitted(packageResponse, statusResponse)) {
        rememberAdmittedPackageId(packageResponse.package_id || id);
      }
    } catch (requestError) {
      setPkg(null);
      setAdmissionStatus(null);
      setError(
        requestError.message ||
          'Package not found or not available for Custodian review.'
      );
    } finally {
      setLoading(false);
    }
  }, [id, live]);

  useEffect(() => {
    loadLive();
  }, [loadLive, reloadToken]);

  const refresh = () => setReloadToken((value) => value + 1);

  const detail = live
    ? pkg
      ? viewModelFromPackage(pkg, admissionStatus)
      : mockDetail
    : mockDetail;
  const headingId = live
    ? pkg?.asset_number || pkg?.reference_number || id
    : id;
  const canDecide = live && canRecordAuthorityDecision(pkg);
  const canAdmit = live && canAdmitPackage(pkg);
  const history = pkg?.review_history || [];
  const admissionState = packageAdmission(pkg, admissionStatus);
  const admitted = live && isPackageAdmitted(pkg, admissionStatus);
  const vaultAssets = admissionStatus?.vault_assets || [];
  const primaryVault = vaultAssets[0];
  const evidenceBundleId =
    pkg?.evidence_bundle_id || primaryVault?.evidence_bundle_id || null;

  const pageClass = live ? 'space-y-5 text-eorig-ink' : 'page-stack';
  const crumbClass = live
    ? 'flex flex-wrap items-center gap-1.5 text-[12px] text-eorig-muted'
    : 'flex flex-wrap items-center gap-2 text-xs text-evault-muted';
  const crumbLinkClass = live
    ? 'font-semibold text-eorig-navy-600 hover:text-eorig-cyan'
    : 'font-semibold text-evault-teal hover:underline';
  const crumbCurrentClass = live
    ? 'min-w-0 truncate font-mono text-eorig-ink'
    : 'font-mono text-umb-neutral-dark';
  const loadingClass = live
    ? 'rounded-2xl border border-eorig-border bg-white p-5 text-sm text-eorig-muted shadow-eorig-card'
    : 'card-shell p-4 text-sm text-evault-muted sm:p-5';
  const shellClass = live
    ? 'rounded-2xl border border-eorig-border bg-white p-5 shadow-eorig-card sm:p-6'
    : 'card-shell p-4 sm:p-5';
  const titleClass = live
    ? 'text-lg font-semibold tracking-tight text-eorig-ink sm:text-xl'
    : 'text-base font-bold tracking-tight text-umb-neutral-dark sm:text-lg';
  const metaClass = live
    ? 'mt-2 text-[13px] leading-relaxed text-eorig-muted'
    : 'mt-2 text-xs leading-relaxed text-evault-muted sm:text-sm';
  const tablistClass = live
    ? 'mt-5 flex flex-wrap gap-1.5'
    : 'mt-5 flex flex-wrap gap-1 border-b border-evault-border pb-px';
  const tabActiveClass = live
    ? 'inline-flex rounded-full bg-eorig-navy-600 px-3 py-1.5 text-xs font-semibold text-white'
    : 'rounded-t-lg px-3 py-2 text-xs font-semibold bg-evault-select-bg text-evault-select-text ring-1 ring-evault-border ring-b-0';
  const tabIdleClass = live
    ? 'inline-flex rounded-full border border-eorig-border bg-white px-3 py-1.5 text-xs font-semibold text-eorig-muted hover:bg-eorig-surface hover:text-eorig-ink'
    : 'rounded-t-lg px-3 py-2 text-xs font-semibold text-umb-neutral-dark/65 hover:bg-evault-surface/60 hover:text-umb-neutral-dark';
  const panelClass = live
    ? 'rounded-2xl border border-eorig-border bg-white p-4 shadow-eorig-card sm:p-5'
    : 'rounded-evault border border-evault-border bg-white p-4 shadow-sm';
  const summaryClass = live
    ? panelClass
    : 'rounded-evault border border-evault-border bg-evault-surface/30 p-4';
  const headingClass = live
    ? 'text-sm font-semibold text-eorig-ink'
    : 'text-sm font-semibold text-umb-neutral-dark';
  const dtClass = live
    ? 'text-[11px] font-semibold uppercase tracking-wide text-eorig-muted'
    : 'font-medium text-evault-muted';
  const ddClass = live
    ? 'mt-0.5 text-sm text-eorig-ink'
    : 'mt-0.5 text-umb-neutral-dark/90';
  const mutedClass = live ? 'text-xs text-eorig-muted' : 'text-xs text-evault-muted';
  const linkClass = live
    ? 'font-semibold text-eorig-navy-600 hover:text-eorig-cyan'
    : 'font-semibold text-evault-teal hover:underline';
  const tabPanelClass = live
    ? 'mt-5 rounded-2xl border border-eorig-border bg-white p-4 shadow-eorig-card sm:p-5'
    : 'mt-5 rounded-evault border border-evault-border bg-white p-4 sm:p-5';
  const metaLabelClass = live ? 'text-eorig-ink/80' : 'text-umb-neutral-dark/80';
  const metaSepClass = live ? 'mx-2 text-eorig-border' : 'mx-2 text-evault-border';
  const bodyTextClass = live ? 'text-sm text-eorig-ink' : 'text-sm text-umb-neutral-dark/85';
  const tabLinkClass = live
    ? 'mt-3 inline-flex text-xs font-semibold text-eorig-navy-600 hover:text-eorig-cyan'
    : 'mt-3 inline-flex text-xs font-semibold text-evault-teal hover:underline';

  const runMutation = async (action) => {
    if (!live || busy) return;
    setBusy(true);
    setActionError('');
    setNotice('');
    try {
      if (action === 'ADMIT') {
        await admitCustodianPackage(id);
      } else {
        await submitCustodianAuthorityDecision(id, action);
      }
      const [packageResponse, statusResponse] = await Promise.all([
        getCustodianPackage(id),
        getCustodianAdmissionStatus(id).catch(() => null),
      ]);
      setConfirm(null);
      setPkg(packageResponse);
      setAdmissionStatus(statusResponse);
      if (isPackageAdmitted(packageResponse, statusResponse)) {
        rememberAdmittedPackageId(packageResponse.package_id || id);
      }
      if (action === 'APPROVED') {
        setNotice(
          'Custodian authority recorded as APPROVED. Custody remains PENDING until technical vault admission.'
        );
      } else if (action === 'REJECTED') {
        setNotice(
          'Custodian authority recorded as REJECTED. This package will not be admitted to the Authoritative vault.'
        );
      } else {
        setNotice(
          'Technical vault admission completed. This package is no longer in Awaiting Review.'
        );
      }
    } catch (requestError) {
      setActionError(requestError.message || 'Unable to complete this Custodian action.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={pageClass}>
      <div className={crumbClass}>
        <Link
          to={
            live && admitted
              ? '/custodian/explorer'
              : live
                ? '/custodian/awaiting-review'
                : '/custodian/assets'
          }
          className={crumbLinkClass}
        >
          {live && admitted
            ? 'Custody explorer'
            : live
              ? 'Awaiting review'
              : 'Asset register'}
        </Link>
        <span aria-hidden>{live ? '›' : '/'}</span>
        <span className={crumbCurrentClass}>{headingId}</span>
      </div>

      {live && error ? (
        <ErrorBanner message={error} onRetry={refresh} />
      ) : null}
      {actionError && !confirm ? <ErrorBanner message={actionError} /> : null}
      {notice ? <NoticeBanner>{notice}</NoticeBanner> : null}

      {live && loading && !pkg ? (
        <section className={loadingClass}>
          Loading…
        </section>
      ) : null}

      {!live || pkg ? (
      <section className={shellClass}>
        <LifecycleInfoPopover
          title="Custodian workflow"
          steps={live && pkg ? custodianWorkflowSteps(pkg, admissionStatus) : []}
        >
          {(lifecycleTrigger) => (
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="flex items-start gap-2">
                  <h2 className={`min-w-0 ${titleClass}`}>
                    Asset {headingId} — {detail.borrower}
                  </h2>
                  {lifecycleTrigger}
                </div>
                <p className={metaClass}>
                  <span className={metaLabelClass}>Originator:</span>{' '}
                  {detail.originator}
                  <span className={metaSepClass}>•</span>
                  <span className={metaLabelClass}>Facility:</span>{' '}
                  {detail.facility}
                  <span className={metaSepClass}>•</span>
                  <span className={metaLabelClass}>
                    Current custody state:
                  </span>{' '}
                  <span className={`font-medium ${live ? 'text-eorig-ink' : 'text-umb-neutral-dark'}`}>
                    {String(detail.custodyState).toLowerCase()}
                  </span>
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                {live && pkg ? (
                  <>
                    <StatusPill
                      value={packageAuthority(pkg)}
                      label={displayValue(packageAuthority(pkg))}
                    />
                    <StatusPill
                      value={packageCustody(pkg)}
                      label={displayValue(packageCustody(pkg))}
                    />
                    {admissionState ? (
                      <StatusPill value={admissionState} />
                    ) : null}
                    {pkg.current_holder ? (
                      <StatusPill value={pkg.current_holder} />
                    ) : null}
                  </>
                ) : (
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide ${badgeClass(detail.badgeVariant)}`}
                  >
                    {detail.custodyState}
                  </span>
                )}
                {canAdmit ? (
                  <Button
                    type="button"
                    variant={live ? 'gradient' : 'navy'}
                    disabled={busy}
                    onClick={() => setConfirm('ADMIT')}
                  >
                    Admit to Authoritative Vault
                  </Button>
                ) : null}
                {!live ? (
                  <button
                    type="button"
                    className="inline-flex rounded-full bg-evault-gold px-4 py-2 text-xs font-semibold text-umb-neutral-dark shadow-sm transition hover:bg-evault-gold-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-evault-gold/40"
                  >
                    Issue certificate
                  </button>
                ) : null}
              </div>
            </div>
          )}
        </LifecycleInfoPopover>

        <div
          className={tablistClass}
          role="tablist"
          aria-label="Asset detail sections"
        >
          {(live ? tabs.filter((t) => t.id !== 'certificates') : tabs).map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={active ? tabActiveClass : tabIdleClass}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'overview' ? (
          <div className="mt-5 space-y-5">
            <div className="grid gap-3 lg:grid-cols-3">
              <div className={summaryClass}>
                <h3 className={headingClass}>
                  Certification summary
                </h3>
                <dl className="mt-3 space-y-2.5 text-xs">
                  {live ? (
                    <>
                      <div>
                        <dt className={dtClass}>Package</dt>
                        <dd className={`${ddClass} font-mono`}>
                          {displayValue(pkg?.reference_number || pkg?.package_id)}
                        </dd>
                      </div>
                      <div>
                        <dt className={dtClass}>Program</dt>
                        <dd className={ddClass}>
                          {displayValue(pkg?.program)}
                        </dd>
                      </div>
                      <div>
                        <dt className={dtClass}>Asset</dt>
                        <dd className={`${ddClass} font-mono`}>
                          {displayValue(pkg?.asset_number || pkg?.asset_id)}
                        </dd>
                      </div>
                    </>
                  ) : null}
                  <div>
                    <dt className={dtClass}>Legal type</dt>
                    <dd className={ddClass}>
                      {detail.legalType}
                    </dd>
                  </div>
                  <div>
                    <dt className={dtClass}>
                      Authority decision
                    </dt>
                    <dd className={ddClass}>
                      {detail.authorityDecision}
                    </dd>
                  </div>
                  {live ? (
                    <>
                      <div>
                        <dt className={dtClass}>
                          Authority status
                        </dt>
                        <dd className={ddClass}>
                          {displayValue(pkg?.authority_status || pkg?.authority_state)}
                        </dd>
                      </div>
                      <div>
                        <dt className={dtClass}>
                          Custody status
                        </dt>
                        <dd className={ddClass}>
                          {displayValue(pkg?.custody_status || pkg?.custody_state)}
                        </dd>
                      </div>
                      <div>
                        <dt className={dtClass}>
                          Admission
                        </dt>
                        <dd className={ddClass}>
                          {displayValue(admissionState)}
                        </dd>
                      </div>
                      <div>
                        <dt className={dtClass}>
                          Provider recommendation
                        </dt>
                        <dd className={ddClass}>
                          {displayValue(pkg?.recommendation || pkg?.provider_decision)}
                        </dd>
                      </div>
                      <div>
                        <dt className={dtClass}>
                          Provider rationale
                        </dt>
                        <dd className={ddClass}>
                          {displayValue(pkg?.provider_rationale)}
                        </dd>
                      </div>
                      <div>
                        <dt className={dtClass}>
                          Provider routing
                        </dt>
                        <dd className={ddClass}>
                          {displayValue(pkg?.task_status)}
                        </dd>
                      </div>
                      <div>
                        <dt className={dtClass}>
                          Vault asset
                        </dt>
                        <dd className={`${ddClass} font-mono`}>
                          {displayValue(primaryVault?.id)}
                        </dd>
                      </div>
                      <div>
                        <dt className={dtClass}>
                          Evidence
                        </dt>
                        <dd className={ddClass}>
                          {evidenceCreationStatus(pkg)}
                          {evidenceBundleId ? (
                            <span className="mt-1 block font-mono text-[11px]">
                              {evidenceBundleId}
                            </span>
                          ) : null}
                        </dd>
                      </div>
                      <div>
                        <dt className={dtClass}>
                          Admission receipt
                        </dt>
                        <dd className={ddClass}>
                          {UNAVAILABLE}
                        </dd>
                      </div>
                    </>
                  ) : null}
                  <div>
                    <dt className={dtClass}>
                      Evidence state
                    </dt>
                    <dd className={ddClass}>
                      {detail.evidenceState}
                    </dd>
                  </div>
                  <div>
                    <dt className={dtClass}>
                      Current holder
                    </dt>
                    <dd className={ddClass}>
                      {detail.holderSummary}
                    </dd>
                  </div>
                  <div>
                    <dt className={dtClass}>
                      Next allowed actions
                    </dt>
                    <dd className={`${ddClass} font-medium ${live ? 'text-eorig-navy-600' : 'text-evault-teal-deep'}`}>
                      {detail.nextAllowed}
                    </dd>
                  </div>
                </dl>
              </div>

              <EvidenceChecklistCard
                items={
                  live && pkg
                    ? custodianEvidenceChecklistItems(pkg, admissionStatus)
                    : evidenceChecklist.map((label) => ({
                        id: label,
                        label,
                        done: true,
                      }))
                }
              />

              <div className={panelClass}>
                <h3 className={headingClass}>
                  Current action panel
                </h3>
                {live ? (
                  <div className="mt-3 space-y-2">
                    {canDecide ? (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="navy"
                          disabled={busy}
                          onClick={() => setConfirm('APPROVED')}
                        >
                          Approve
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          className="text-red-600"
                          disabled={busy}
                          onClick={() => setConfirm('REJECTED')}
                        >
                          Reject
                        </Button>
                      </div>
                    ) : null}
                    {canAdmit ? (
                      <div>
                        <Button
                          type="button"
                          variant={live ? 'gradient' : 'navy'}
                          disabled={busy}
                          onClick={() => setConfirm('ADMIT')}
                        >
                          Admit to Authoritative Vault
                        </Button>
                        <p className={`mt-2 text-[11px] ${live ? 'text-eorig-muted' : 'text-evault-muted'}`}>
                          Creates the authoritative copy and associated evidence
                          and admission receipt.
                        </p>
                      </div>
                    ) : null}
                    {!canDecide && !canAdmit ? (
                      <p className={mutedClass}>
                        {pkg?.authority_status === 'REJECTED'
                          ? 'Custodian authority is REJECTED. Admission is not available.'
                          : pkg?.admission_state === 'AUTHORITATIVE'
                            ? 'This package has been admitted to the Authoritative vault.'
                            : 'No Custodian authority or admission action is available.'}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                <ul className="mt-3 space-y-2 text-xs">
                  {(live
                    ? actionLinks.filter((a) => a.to === '/custodian/release-copy')
                    : actionLinks
                  ).map((a) =>
                    a.to ? (
                      <li key={a.label}>
                        <Link
                          to={a.to}
                          className={linkClass}
                        >
                          {a.label}
                        </Link>
                      </li>
                    ) : (
                      <li key={a.label}>
                        <button
                          type="button"
                          className={linkClass}
                        >
                          {a.label}
                        </button>
                      </li>
                    )
                  )}
                </ul>
                <div className={`mt-4 border-t pt-3 text-[11px] ${live ? 'border-eorig-border text-eorig-muted' : 'border-evault-border text-evault-muted'}`}>
                  Bundle{' '}
                  {live ? (
                    <span className="font-mono font-medium">
                      {evidenceBundleId || UNAVAILABLE}
                    </span>
                  ) : (
                    <Link
                      to="/custodian/evidence/bundles"
                      className="font-mono font-medium text-evault-teal hover:underline"
                    >
                      {detail.bundleRef}
                    </Link>
                  )}
                  {' · '}
                  Receipt{' '}
                  {live ? (
                    <span className="font-mono font-medium">{detail.receiptRef}</span>
                  ) : (
                    <Link
                      to="/custodian/evidence/receipts"
                      className="font-mono font-medium text-evault-teal hover:underline"
                    >
                      {detail.receiptRef}
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {live && pkg ? (
              <CustodianSourceEvidencePanel
                pkg={pkg}
                admissionStatus={admissionStatus}
                showChecklist={false}
              />
            ) : null}

            <div className={live ? panelClass : 'rounded-evault border border-evault-border bg-evault-surface/20 p-4 sm:p-5'}>
              <h3 className={headingClass}>
                Custody timeline
              </h3>
              <p className={`mt-1 ${mutedClass}`}>
                {live
                  ? 'State transitions returned for this package.'
                  : 'State transitions for this asset (mock series).'}
              </p>
              <div className="mt-6">
                <HorizontalCustodyTimeline steps={detail.timeline} />
              </div>
            </div>
          </div>
        ) : null}

        {tab === 'evidence' ? (
          <div className={tabPanelClass}>
            {live ? (
              <>
                <p className={bodyTextClass}>
                  {evidenceCreationStatus(pkg)}
                  {evidenceBundleId
                    ? ` Evidence bundle ${evidenceBundleId}.`
                    : ` Evidence bundle ${UNAVAILABLE}.`}
                </p>
                <CustodianSourceEvidencePanel
                  pkg={pkg}
                  admissionStatus={admissionStatus}
                />
              </>
            ) : (
              <>
                <EvidenceChecklistCard
                  items={evidenceChecklist.map((label) => ({
                    id: label,
                    label,
                    done: true,
                  }))}
                />
                <p className={bodyTextClass}>
                  Evidence artifacts, validation reports, and sealed bundle lineage for{' '}
                  <span className="font-mono font-medium">{detail.bundleRef}</span>.
                </p>
              </>
            )}
            {live ? null : (
              <Link
                to="/custodian/evidence/bundles"
                className={tabLinkClass}
              >
                Open evidence bundle →
              </Link>
            )}
          </div>
        ) : null}

        {tab === 'timeline' ? (
          <div className={tabPanelClass}>
            <HorizontalCustodyTimeline steps={detail.timeline} />
          </div>
        ) : null}

        {tab === 'certificates' && !live ? (
          <div className={tabPanelClass}>
            <p className="text-sm text-umb-neutral-dark/85">
              Issued and superseded certificates for this asset.
            </p>
            <Link
              to="/custodian/evidence/certificate-history"
              className={tabLinkClass}
            >
              Open certificate history →
            </Link>
          </div>
        ) : null}

        {tab === 'related' ? (
          <div className={tabPanelClass}>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  className={linkClass}
                  to="/custodian/release-copy"
                >
                  Release & certified copy
                </Link>
              </li>
              <li>
                <Link
                  className={linkClass}
                  to={`/custodian/explorer`}
                >
                  Back to custody explorer
                </Link>
              </li>
            </ul>
          </div>
        ) : null}

        {tab === 'audit' ? (
          live ? (
            <div className="mt-5 overflow-hidden rounded-2xl border border-eorig-border bg-white shadow-eorig-card">
              <table className="w-full border-collapse text-left text-xs">
                <thead>
                  <tr className="border-b border-eorig-border bg-eorig-surface">
                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">
                      When
                    </th>
                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">
                      Actor
                    </th>
                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">
                      Event
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {history.length > 0 ? (
                    history.map((item, index) => (
                      <tr
                        key={item.id}
                        className={`border-b border-eorig-border last:border-b-0 ${
                          index % 2 === 1 ? 'bg-eorig-surface/60' : 'bg-white'
                        }`}
                      >
                        <td className="px-4 py-3 text-eorig-muted">
                          {formatTimestamp(item.created_at)}
                        </td>
                        <td className="px-4 py-3 font-mono text-eorig-ink">
                          {displayValue(item.decided_by)}
                        </td>
                        <td className="px-4 py-3 text-eorig-ink">
                          {item.is_authority_check ? 'Checklist ' : ''}
                          {displayValue(item.decision)}
                          {item.rationale ? ` · ${item.rationale}` : ''}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr className="bg-white">
                      <td className="px-4 py-3 text-eorig-muted" colSpan={3}>
                        {UNAVAILABLE}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
          <div className="mt-5 overflow-hidden rounded-evault border border-evault-border">
            <table className="w-full border-collapse text-left text-xs">
              <thead>
                <tr className="border-b border-evault-border bg-evault-surface/80">
                  <th className="px-4 py-2.5 font-semibold text-evault-muted">
                    When
                  </th>
                  <th className="px-4 py-2.5 font-semibold text-evault-muted">
                    Actor
                  </th>
                  <th className="px-4 py-2.5 font-semibold text-evault-muted">
                    Event
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-evault-border bg-white">
                  <td className="px-4 py-3 text-evault-muted">Mar 21 09:12</td>
                  <td className="px-4 py-3">custody.ops@umb.com</td>
                  <td className="px-4 py-3">Viewed certification detail</td>
                </tr>
                <tr className="border-b border-evault-border bg-evault-surface/35">
                  <td className="px-4 py-3 text-evault-muted">Mar 20 14:02</td>
                  <td className="px-4 py-3">system</td>
                  <td className="px-4 py-3">Receipt {detail.receiptRef} issued</td>
                </tr>
              </tbody>
            </table>
          </div>
          )
        ) : null}
      </section>
      ) : null}

      <CustodianConfirmModal
        open={Boolean(confirm)}
        title={
          confirm === 'ADMIT'
            ? 'Admit to Authoritative Vault'
            : confirm === 'REJECTED'
              ? 'Reject authority'
              : 'Approve authority'
        }
        description={
          confirm === 'ADMIT'
            ? 'Creates the authoritative copy and associated evidence and admission receipt.'
            : confirm === 'REJECTED'
              ? 'This records the Custodian authority decision. The package will not be admitted to the Authoritative vault.'
              : 'This records the Custodian authority decision. The package will still require technical vault admission.'
        }
        confirmLabel={
          confirm === 'ADMIT'
            ? 'Admit to Authoritative Vault'
            : confirm === 'REJECTED'
              ? 'Reject'
              : 'Approve'
        }
        busy={busy}
        error={actionError}
        onCancel={() => setConfirm(null)}
        onConfirm={() => runMutation(confirm)}
      />
    </div>
  );
}
